import { useState } from "react";
import { ChevronRight } from "lucide-react";

const slug = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

const ComingSoonHint = () => (
  <span className="ml-auto text-[11px] italic text-[#9CA3AF] tracking-normal whitespace-nowrap">
    Coming soon
  </span>
);

const NavIcon = ({ icon: Icon, muted = false }) => {
  if (!Icon) return null;
  return (
    <Icon
      className={`w-4 h-4 shrink-0 mr-2.5 ${muted ? "text-[#C7CBD1]" : "text-[#6B7280]"}`}
      strokeWidth={1.5}
      aria-hidden="true"
    />
  );
};

const PAD = ["pl-0", "pl-4", "pl-7", "pl-10", "pl-12"];

/**
 * Flatten a nav tree into depth-stamped rows with ancestor labels. Visibility
 * is computed at render time from an openSet of toggled ancestors — avoids a
 * recursive React component (which previously tripped a Babel/react-refresh
 * stack-overflow loop).
 */
function flattenNav(items, depth, parents, out) {
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    out.push({
      label: item.label,
      href: item.href,
      icon: item.icon,
      comingSoon: !!item.comingSoon,
      external: !!item.external,
      depth,
      parents,
      hasChildren,
    });
    if (hasChildren) {
      flattenNav(item.children, depth + 1, parents.concat(item.label), out);
    }
  }
}

function buildRows(items) {
  const out = [];
  flattenNav(items, 0, [], out);
  return out;
}

function MobileNavTree({ items, onNavigate }) {
  const [openSet, setOpenSet] = useState(() => new Set());
  const toggle = (label) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const rows = buildRows(items);
  const baseRow =
    "w-full flex items-center text-left text-sm font-medium text-[#0F172A] hover:text-[#025979] py-2.5";

  return (
    <div className="flex flex-col">
      {rows.map((row) => {
        const visible = row.parents.every((p) => openSet.has(p));
        if (!visible) return null;

        const padLeft = PAD[row.depth] || "pl-12";
        const isOpen = openSet.has(row.label);
        const key = `${row.parents.join(">")}>${row.label}`;

        if (row.hasChildren) {
          return (
            <div key={key} className="border-b border-[#F3F4F6] last:border-b-0">
              <div className={`flex items-center ${padLeft}`}>
                {row.href ? (
                  <a
                    href={row.href}
                    onClick={onNavigate}
                    className={baseRow}
                    data-testid={`mobile-nav-${slug(row.label)}`}
                  >
                    <NavIcon icon={row.icon} />
                    <span>{row.label}</span>
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => toggle(row.label)}
                    className={baseRow}
                    data-testid={`mobile-nav-${slug(row.label)}`}
                  >
                    <NavIcon icon={row.icon} />
                    <span>{row.label}</span>
                  </button>
                )}
                <button
                  type="button"
                  aria-label={`Toggle ${row.label}`}
                  onClick={() => toggle(row.label)}
                  className="ml-auto p-2 text-[#6B7280] hover:text-[#025979]"
                  data-testid={`mobile-nav-toggle-${slug(row.label)}`}
                >
                  <ChevronRight
                    className={`w-4 h-4 transition-transform ${isOpen ? "rotate-90" : ""}`}
                  />
                </button>
              </div>
            </div>
          );
        }

        if (row.comingSoon) {
          return (
            <div
              key={key}
              className={`${padLeft} flex items-center text-sm text-[#9CA3AF] py-2.5`}
              data-testid={`mobile-nav-${slug(row.label)}`}
            >
              <NavIcon icon={row.icon} muted />
              <span>{row.label}</span>
              <ComingSoonHint />
            </div>
          );
        }

        return (
          <a
            key={key}
            href={row.href}
            target={row.external ? "_blank" : undefined}
            rel={row.external ? "noopener noreferrer" : undefined}
            onClick={onNavigate}
            className={`${padLeft} ${baseRow}`}
            data-testid={`mobile-nav-${slug(row.label)}`}
          >
            <NavIcon icon={row.icon} />
            <span>{row.label}</span>
          </a>
        );
      })}
    </div>
  );
}

export default MobileNavTree;
